import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Requests the drawer to open as soon as the page loads. Set by the cart page redirect in
 * layout/theme.liquid — keep the two in sync — and usable by hand to deep link to an open drawer.
 */
const OPEN_ON_LOAD_HASH = '#cart-drawer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /**
   * The footer height measured at open time is not final — `<shopify-accelerated-checkout>`
   * renders its buttons asynchronously — so the sticky state follows the footer as it settles.
   * @type {ResizeObserver}
   */
  #summaryResizeObserver = new ResizeObserver(() => this.#updateStickyState());

  /** @type {Element | null} */
  #observedSummary = null;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopObservingSummary);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    this.#openOnLoadIfRequested();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopObservingSummary);
    this.#stopObservingSummary();
    this.#historyAbortController?.abort();
  }

  /**
   * Opens the drawer on load when the URL carries the open-on-load hash. The hash is dropped first,
   * so a reload or a copied link doesn't reopen the drawer.
   */
  #openOnLoadIfRequested() {
    if (window.location.hash !== OPEN_ON_LOAD_HASH) return;

    const { pathname, search } = window.location;
    history.replaceState(null, '', `${pathname}${search}`);

    this.open();
  }

  /**
   * Pushes the mobile history entry before the base class locks the body. `showDialog()` pins the
   * body with `position: fixed`, which collapses `window.scrollY` to 0, and the browser stores the
   * current entry's scroll position when a new entry is pushed on top of it. Pushing after the lock
   * recorded 0, so `history.back()` on close restored the page to the top.
   */
  showDialog() {
    if (!this.refs.dialog?.open) this.#pushHistoryEntry();
    super.showDialog();
  }

  #pushHistoryEntry() {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  }

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // The event is dispatched before `morphSection` swaps the cart markup, so wait a frame for the
    // new footer. An opening drawer needs no nudge — `dialog:open` measures it.
    if (this.refs.dialog?.open) {
      requestAnimationFrame(() => this.#updateStickyState());
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    this.#observeSummary(summary);

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Points the resize observer at the current summary. A re-render can morph in a different node,
   * so the observer is moved rather than attached once.
   *
   * @param {Element | null} summary - The summary element, or null when the cart is empty.
   */
  #observeSummary(summary) {
    if (summary === this.#observedSummary) return;

    this.#summaryResizeObserver.disconnect();
    this.#observedSummary = summary;
    if (summary) this.#summaryResizeObserver.observe(summary);
  }

  #stopObservingSummary = () => {
    this.#summaryResizeObserver.disconnect();
    this.#observedSummary = null;
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
